import React, { Fragment } from 'react';
import { SecondaryText } from 'Components/Texts';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { SHEER_FIRST_RESPONDER_TYPE, SHEER_MILITARY_TYPE } from 'Utils/Constants';
import { SmartLink } from 'Components/SmartLink';
import { getDefaultConfig } from 'Utils/Store';
import { getSheerIdProgram } from 'Utils/SheerIdHelpers';

const TermsOfServiceLink = (props) => {
  const { termsOfUsePage } = getDefaultConfig();
  return <SmartLink linkUrl={termsOfUsePage} target="noopener noreferrer" reload {...props} />;
};
const PrivacyPolicyLink = (props) => {
  const { privacyPolicyPage } = getDefaultConfig();
  return <SmartLink linkUrl={privacyPolicyPage} target="noopener noreferrer" reload {...props} />;
};
export const SheerIdHelpLink = (props) => {
  let sheerProgramType = getSheerIdProgram();
  if (!sheerProgramType) {
    const urlParams = new URLSearchParams(location.search);
    sheerProgramType = urlParams.get('sheerVerification') || SHEER_MILITARY_TYPE;
  }

  const { sheerIdHelpMilitaryPage, sheerIdHelpFirstResponderPage } = getDefaultConfig();
  const linkUrl =
    sheerProgramType === SHEER_FIRST_RESPONDER_TYPE ? sheerIdHelpFirstResponderPage : sheerIdHelpMilitaryPage;
  return <SmartLink linkUrl={linkUrl} target="noopener noreferrer" reload {...props} />;
};

export const TosAndPrivacyFinePrint = ({ ctaText = '' }) => {
  if (ctaText) {
    const tosAndPrivacyText = (
      <Fragment>
        By clicking &quot;{ctaText}&quot;, you agree to the{' '}
        <TermsOfServiceLink
          text="Terms of Use"
          onClick={() => trackEvent(TRACKING_ENUMS.EVENT_TERMS_OF_SERVICE, null, { profile_start_source: 'signup' })}
          className="blueLink"
        />
        {', '}
        <PrivacyPolicyLink
          text="Privacy Policy"
          onClick={() => trackEvent(TRACKING_ENUMS.EVENT_PRIVACY_POLICY, null, { profile_start_source: 'signup' })}
          className="blueLink"
        />
        , and agree to receive content and promotional communications
      </Fragment>
    );
    return <SecondaryText className="legalText" alignText="centerText" text={tosAndPrivacyText} />;
  }
  return null;
};

export const MarineFinePrint = ({ ctaText = '' }) => {
  if (ctaText) {
    const marineLegalText = (
      <Fragment>
        By clicking &quot;{ctaText}&quot;, you agree to the{' '}
        <TermsOfServiceLink
          text="Terms of Use"
          onClick={() => trackEvent(TRACKING_ENUMS.EVENT_TERMS_OF_SERVICE)}
          className="finePrintLink"
        />
        {', '}
        <PrivacyPolicyLink
          text="Privacy Policy"
          onClick={() => trackEvent(TRACKING_ENUMS.EVENT_PRIVACY_POLICY)}
          className="finePrintLink"
        />
        , and agree to receive content and promotional communications from FOX NEWS.
      </Fragment>
    );
    return <p className="finePrint centerText">{marineLegalText}</p>;
  }
  return null;
};

export const RedeemGiftHomeFinePrint = ({ className = '' }) => {
  return (
    <p className={className}>
      Gift subscriptions are valid only toward the Fox Nation streaming service in the United States. The Fox Nation
      service is not available in other countries outside of the United States and gift subscriptions are not redeemable
      outside the United States. New users must create an account to redeem. Existing users can apply a gift
      subscription only to an existing paid account that was created on Foxnation.com. Gift subscriptions cannot be
      applied to trial accounts. Gift subscriptions cannot be applied to Fox Nation accounts purchased on Comcast or
      other third-party TV providers that offer Fox Nation directly to their subscribers. Gift subscriptions cannot be
      applied to existing Fox Nation accounts purchased through the Fox Nation apps on the following platforms: iOS,
      Android, Apple TV, Xbox, Roku, Fire TV or TVOS. A maximum of three gift subscriptions can be credited to an
      account at a time. Entire value is credited to your Fox Nation account upon redemption. Gift subscriptions are
      nonrefundable and are not redeemable for cash, except where required by law. Gift subscription codes cannot be
      resold or replaced if they are misplaced, lost or stolen. Fox News Network, LLC is not responsible for misplaced,
      lost or stolen codes or for unauthorized use. When the Fox Nation gift subscription period has ended, access to
      Fox Nation will end unless you provide another payment method and sign up for a paid subscription plan. The
      merchandise associated with each subscription or gift subscription tier is subject to change. Visit{' '}
      <SmartLink
        linkUrl="https://nation.foxnews.com/gift-terms"
        text="https://nation.foxnews.com/gift-terms"
        className="finePrintLink"
        reload
        target="noopener noreferrer"
      />{' '}
      for full terms and conditions.
    </p>
  );
};

export const RedeemGiftFinePrint = ({ className = '' }) => {
  return (
    <Fragment>
      <p className={`finePrint ${className}`}>
        This card will be redeemed to the account you are currently signed into. If you want it applied to a new or
        different account, sign out and start the redemption process over.
      </p>
      <p className={`finePrint ${className}`}>
        Learn how to find your gift code{' '}
        <SmartLink
          linkUrl="https://help.fox.com/s/article/How-Do-I-Redeem-My-Fox-Nation-Gift-Card"
          text="here"
          className="finePrintLink"
          target="noopener noreferrer"
          reload
        />
        .
      </p>
      <p className={`finePrint ${className}`}>
        If you need additional help, please contact{' '}
        <SmartLink
          linkUrl="https://help.fox.com"
          text="Customer Support"
          className="finePrintLink"
          target="noopener noreferrer"
          reload
        />
        .
      </p>
    </Fragment>
  );
};

export const RedeemGiftTrialistFinePrint = ({ className = '' }) => {
  return <p className={`finePrint ${className}`}>Please redeem your Gift card at the end of your trial.</p>;
};

export const RedeemGiftIAPFinePrint = ({ className = '' }) => {
  return (
    <p className={`finePrint ${className}`}>
      Gift subscriptions cannot be applied to existing Fox Nation accounts purchased through the Fox Nation apps on iOS,
      Android, Roku, Fire, TV or tvOS. See our{' '}
      <SmartLink
        linkUrl="http://nation.foxnews.com/gift-terms"
        text="Gift Terms"
        className="finePrintLink"
        target="noopener noreferrer"
        reload
      />{' '}
      for more information.
    </p>
  );
};

export const ReCaptchaFinePrint = ({ className = '' }) => {
  return (
    <p className={`finePrint ${className}`} data-testid="recaptchaFinePrint">
      This site is protected by reCAPTCHA and the Google{' '}
      <SmartLink
        linkUrl="https://policies.google.com/privacy"
        text="Privacy Policy"
        className="finePrintLink"
        target="noopener noreferrer"
        reload
      />{' '}
      and{' '}
      <SmartLink
        linkUrl="https://policies.google.com/terms"
        text="Terms of Service"
        className="finePrintLink"
        target="noopener noreferrer"
        reload
      />{' '}
      apply.
    </p>
  );
};

export const ChangePlanPaymentFinePrint = () => {
  return (
    <div className="finePrint paymentFinePrint">
      When you upgrade to another plan, it will take effect immediately. Any time left in your current plan will be
      added to your new plan. When you downgrade your plan, your new plan will take effect once your current
      subscription ends.
    </div>
  );
};

export const PaymentPlanFinePrint = ({ ctaText, sheerIdProgram, selectedPlanId }) => {
  if (sheerIdProgram === SHEER_MILITARY_TYPE) {
    return (
      <div className="finePrint paymentFinePrint">
        *Subscription fees are non-refundable. After a free trial or discount period ends, your selected subscription
        will convert into a paid subscription at the then-current rates and will automatically renew, unless you cancel
        before the conversion or renewal. For one-year free Military subscription plans: after your original
        subscription expires, it will automatically renew for additional one-year periods at fifty percent off the
        then-current rates unless you cancel before the renewal. Renewal charges will be applied to the payment method
        associated with your account, and you hereby authorize Fox News Network, LLC or its designee to make such
        charge. You must cancel at least 24 hours prior to your auto-renewal date if you do not wish to renew.
        Otherwise, your payment method will be charged for the new subscription period. To cancel, sign into your
        account and select &quot;Cancel Plan&quot; from the My Account page. By selecting {ctaText} below, you agree to
        these recurring charges and accept the <TermsOfServiceLink text="Terms of Use" className="finePrintLink" /> and{' '}
        <PrivacyPolicyLink text="Privacy Policy" className="finePrintLink" />.
      </div>
    );
  }
  if (sheerIdProgram === SHEER_FIRST_RESPONDER_TYPE) {
    return (
      <div className="finePrint paymentFinePrint">
        *Subscription fees are non-refundable. After a free trial or discount period ends, your selected subscription
        will convert into a paid subscription at the then-current rates and will automatically renew, unless you cancel
        before the conversion or renewal. For one-year free First Responder subscription plans: after your original
        subscription expires, it will automatically renew for additional one-year periods at fifty percent off the
        then-current rates unless you cancel before the renewal. Renewal charges will be applied to the payment method
        associated with your account, and you hereby authorize Fox News Network, LLC or its designee to make such
        charge. You must cancel at least 24 hours prior to your auto-renewal date if you do not wish to renew.
        Otherwise, your payment method will be charged for the new subscription period. To cancel, sign into your
        account and select &quot;Cancel Plan&quot; from the My Account page. By selecting {ctaText} below, you agree to
        these recurring charges and accept the <TermsOfServiceLink text="Terms of Use" className="finePrintLink" /> and{' '}
        <PrivacyPolicyLink text="Privacy Policy" className="finePrintLink" />.
      </div>
    );
  }
  return (
    <div className="finePrint paymentFinePrint">
      Promotional Offers are available only to new and returning Fox Nation subscribers on qualifying Fox Nation
      subscription plans. If a promo code is required for discount, it must be applied at time of purchase to receive
      the promotional offer. Promotional offers are valid only for a limited time, are subject to change without notice,
      and cannot be combined with any other discount or promotional offer. If a free trial is offered, the subscription
      will automatically convert into a paid subscription at the end of the free trial, and the subscription fee will be
      billed at the then-current rate at the end of the free trial. If a promotional discount is offered, the discounted
      subscription fee will be billed immediately. After your discount period expires, your subscription will
      automatically convert into a full price subscription at the then-current rate and will automatically renew for
      additional periods at the then-current rate. Annual and multi-year subscription plans will automatically renew for
      additional one-year periods on a recurring basis at the then-current rates, unless you cancel before the renewal.
      Monthly subscription plans will automatically each month on a recurring basis at the then-current rates, unless
      you cancel before the renewal. Subscription fees are non-refundable. Renewal charges will be applied to the
      payment method associated with your account, and you hereby authorize Fox News Network, LLC or its designee to
      make such charge. To cancel or prevent the automatic conversion of your subscription, sign into your account and
      select &quot;Cancel Plan&quot; on the My Account page. By signing up, you agree to these recurring charges and
      accept the Fox Nation <TermsOfServiceLink text="Terms of Use" className="finePrintLink" /> and{' '}
      <PrivacyPolicyLink text="Privacy Policy" className="finePrintLink" />.
    </div>
  );
};

export const VerificationFinePrint = () => (
  <div className="finePrint sheerIdFinePrint">
    By clicking, I consent to processing my information for the purpose of verifying my eligibility as described within
    the <PrivacyPolicyLink text="Fox Nation Privacy Policy" className="finePrintLink highlighted" />. I understand that
    my information will be shared with Fox Nation. Verification services powered by SheerID{' '}
    <SheerIdHelpLink text="SheerID Help Center" className="finePrintLink highlighted" />.
  </div>
);

export const VerificationReviewFinePrint = () => (
  <div className="finePrint sheerIdFinePrint">
    Verification services powered by SheerID{' '}
    <SheerIdHelpLink text="SheerID Help Center" className="finePrintLink highlighted" />.
  </div>
);
